import { RoleResponse, UpdateUserRolesRequest } from "@api/apiClient";
import { useRoles, useUpdateUserRoles } from "@api/roles";
import { useUser, useUserPermissions } from "@api/users";
import i18n from "@i18n/i18n";
import { Button, Card, CardContent, CardHeader, Checkbox, Container, Grid, List, ListItem, ListItemButton, ListItemText, Typography, useTheme } from "@mui/material";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

export interface UserDetailsProps {}

const UserDetails = observer(() => {
  const theme = useTheme();
  const { id: userId } = useParams();
  const [checked, setChecked] = useState<string[]>([]);
  const user = useUser(userId!);
  const roles = useRoles();
  const updateUserRoles = useUpdateUserRoles(userId!);

  const [permissions, setPermissions] = useState<string[]>([]);
  const userPermissions = useUserPermissions(userId);

  useEffect(() => {
    const newPermissions = userPermissions.data?.filter((x) => x.type === "permission").map((x) => x.value!) || [];
    setPermissions(newPermissions.sort());
  }, [userPermissions.data]);

  useEffect(() => {
    if (user.data) {
      setChecked(user.data.roles || []);
    }
  }, [user.data]);

  const handleToggle = (role: string) => () => {
    const currentIndex = checked.indexOf(role);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(role);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const handleUpdateUser = async () => {
    const updateUserRolesRequest = new UpdateUserRolesRequest({
      userId: userId!,
      roles: checked,
    });
    await updateUserRoles.mutateAsync(updateUserRolesRequest);
    await userPermissions.refetch();
  };

  return (
    <Container maxWidth={false} sx={{ height: "100%" }}>
      <Grid mt={2} container spacing={2} sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Grid size={12}>
          <Typography variant="h6" fontWeight="bold">
            {user.data?.name}
          </Typography>
          <Typography variant="subtitle1" color={theme.palette.grey[500]} fontSize="0.75rem">
            {i18n.t("user-details.sub-title")}
          </Typography>
        </Grid>

        <Grid size={12} sx={{ display: "flex", width: "100%", position: "relative" }}>
          <Card elevation={3} sx={{ width: "100%", "& .MuiCardHeader-root": { backgroundColor: theme.palette.background.default }, border: 1, borderColor: theme.palette.divider }}>
            <CardHeader title={<Typography variant="body2">{i18n.t("user-details.profile")}</Typography>} elevation={3} />
            <CardContent sx={{ display: "flex", alignItems: "center", borderTop: 1, borderColor: theme.palette.divider }}>
              <Grid container spacing={2} sx={{ width: "100%" }}>
                <Grid size={8}>
                  <Typography variant="caption">{i18n.t("user-details.email-title")}</Typography>
                  <Typography variant="body1">{user.data?.email}</Typography>
                </Grid>
                <Grid size={2}>
                  <Typography variant="caption">{i18n.t("user-details.status")}</Typography>
                  <Typography variant="body1">{"TBD"}</Typography>
                </Grid>
                <Grid size={2}>
                  <Typography variant="caption">{i18n.t("user-details.created-title")}</Typography>
                  <Typography variant="body1">{i18n.t("datetime_short", { value: user.data?.createdAt })}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={12} sx={{ display: "flex", width: "100%", position: "relative" }}>
          <Card elevation={3} sx={{ width: "100%", "& .MuiCardHeader-root": { backgroundColor: theme.palette.background.default }, border: 1, borderColor: theme.palette.divider }}>
            <CardHeader title={<Typography variant="body2">{i18n.t("user-details.roles")}</Typography>} elevation={3} />
            <CardContent sx={{ display: "flex", alignItems: "center", borderTop: 1, borderColor: theme.palette.divider }}>
              <List sx={{ width: "100%" }}>
                {roles.data?.roles?.map((role: RoleResponse) => {
                  return (
                    <ListItem key={role.name} secondaryAction={<Checkbox edge="end" checked={checked.includes(role.name!)} />} onClick={handleToggle(role.name!)} disablePadding>
                      <ListItemButton>
                        <ListItemText primary={role.name} />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={12} sx={{ display: "flex", width: "100%", position: "relative" }}>
          <Card elevation={3} sx={{ width: "100%", "& .MuiCardHeader-root": { backgroundColor: theme.palette.background.default }, border: 1, borderColor: theme.palette.divider }}>
            <CardHeader title={<Typography variant="body2">{i18n.t("user-details.permissions")}</Typography>} elevation={3} />
            <CardContent sx={{ display: "flex", alignItems: "center", borderTop: 1, borderColor: theme.palette.divider }}>
              <List sx={{ width: "100%" }}>
                {permissions?.map((permission: string) => {
                  return (
                    <ListItem key={permission} disablePadding>
                      <ListItemText primary={permission} />
                    </ListItem>
                  );
                })}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid size="auto" offset={{ xs: "auto" }}>
          <Button variant="contained" onClick={handleUpdateUser} loading={updateUserRoles.isPending}>
            {i18n.t("user-details.save")}
          </Button>
        </Grid>
      </Grid>
    </Container>
  );
});

export default UserDetails;
